import React from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import i18n from 'config/i18n'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import { MediaCell } from 'components/Shared/MuiTable/Cell/MediaCell'
import { ActionTextCell } from 'components/Shared/MuiTable/Cell/RouteLinkCell'
import { BannerState, BANNER_STATUS, TimeWindowState } from 'state/banners'
import { MediaData } from 'components/MediaViewer'
import { theme } from 'theme'
import { parseBackendDate } from 'services/dates'
import { Country } from 'services/api/constants'

interface StringCellProps {
    value: string
    color?: string
}

export interface RowsProps {
    id: string
    name: StringCellProps
    image: MediaData
    duration: StringCellProps
    status: StringCellProps
}

export const getColumns = (): GridColDef[] => {
    const cols: GridColDef[] = [
        {
            field: 'name',
            headerName: i18n.t('submission.table.bannerNameLabel', 'Banner Name'),
            minWidth: 140,
            flex: 1,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            sortable: false,
        },
        {
            field: 'image',
            headerName: i18n.t('submission.table.imageLabel', 'Image'),
            minWidth: 120,
            flex: 1,
            renderCell: function Media(params: GridRenderCellParams): React.ReactElement {
                const style = {
                    maxHeight: '80px',
                    objectFit: 'contain',
                }
                return <MediaCell {...params} style={style} />
            },
            sortable: false,
            filterable: false,
        },
        {
            field: 'duration',
            headerName: i18n.t('submission.table.durationLabel', 'Duration'),
            minWidth: 140,
            flex: 1,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            sortable: false,
            filterable: false,
        },
        {
            field: 'status',
            headerName: i18n.t('submission.table.statusLabel'),
            minWidth: 140,
            flex: 1,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            sortable: false,
            filterable: false,
        },
        {
            field: 'action',
            type: 'string',
            headerName: i18n.t('submission.table.actionsLabel'),
            width: 165,
            renderCell: (props: GridRenderCellParams) => <ActionTextCell {...props} />,
            sortable: false,
            filterable: false,
        },
    ]
    return cols
}

export const getRows = (
    banners: BannerState[],
    bannerGroupUuid: string,
    activePageNum: number,
    country: Country | null,
): RowsProps[] => {
    const getBannerDisplayStatus = (status?: BANNER_STATUS): StringCellProps => {
        return status === BANNER_STATUS.LIVE
            ? {
                  value: i18n.t('statusLabel.live', 'Live'),
                  color: theme.colors.monza,
              }
            : status === BANNER_STATUS.SCHEDULED
              ? {
                    value: i18n.t('statusLabel.scheduled', 'Scheduled'),
                    color: theme.colors.jungleGreen,
                }
              : status === BANNER_STATUS.ENDED
                ? {
                      value: i18n.t('statusLabel.ended', 'Ended'),
                      color: theme.newColors.neutral400,
                  }
                : {
                      value: i18n.t('statusLabel.disabled', 'Disabled'),
                      color: theme.newColors.neutral400,
                  }
    }
    const getDurationDisplayStatus = (
        country: Country | null,
        duration: TimeWindowState,
        status?: BANNER_STATUS,
    ): StringCellProps => {
        const value = country
            ? parseBackendDate(duration.start, country) + ' - ' + parseBackendDate(duration.end, country)
            : ''
        return status === BANNER_STATUS.LIVE
            ? {
                  value: value,
                  color: theme.colors.mineShaft,
              }
            : status === BANNER_STATUS.SCHEDULED
              ? {
                    value: value,
                    color: theme.colors.mineShaft,
                }
              : status === BANNER_STATUS.ENDED
                ? {
                      value: value,
                      color: theme.newColors.neutral400,
                  }
                : {
                      value: value,
                      color: theme.newColors.neutral400,
                  }
    }
    return banners.map((banner: BannerState, index: number) => {
        const url = `/banners/editBanner?bannerGroup=${bannerGroupUuid}&banner=${banner.uuid}&page=${activePageNum}`
        return {
            id: banner.uuid,
            name: { value: banner.name },
            image: banner.image,
            duration: getDurationDisplayStatus(country, banner.time_window, banner.status),
            status: getBannerDisplayStatus(banner.status),
            action: {
                url,
                actionTitle: i18n.t('button.viewBannerDetails', 'View Banner Details'),
            },
        }
    })
}
